import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Signature } from '@/types/signature';
import { useMockSign } from '@/hooks/use-mock-sign';
import { getFrontendEnvVariables } from '@mls-wallet/env';
import { useAuthClient } from '@/lib/authClient';
import { SuccessDialogState } from '../transaction-builder/enums';
import {
  AccountTransactionDecision,
  AnySignableDecision,
  OrgOperationDecision,
} from '@mls-wallet/shared-types';

interface PendingSignable {
  id: string;
  // When reviewing a signable, we will check if approvals/rejections have reach threshold
  approvals: Signature[];
  rejections: Signature[];
  requiredApprovals: number;
  // When initiating a new transaction, we need to check if the initiator's signature exists
  initiatorSignature: Signature | undefined;
}

interface PendingSignaturesModalProps {
  open: boolean;
  refreshSignables: () => void;
  resetPendingState: (result?: 'signed' | 'cancelled') => void;
  pendingSignableIds: string[];
  polledSignables: PendingSignable[];
  pendingDecision: AnySignableDecision | null;
  hookFunctionToPoll: (options: { refreshInterval: number }) => void;
  isTransaction: boolean;
  onSuccess?: (state: SuccessDialogState) => void;
}

export function PendingSignaturesModal({
  open,
  resetPendingState,
  refreshSignables,
  pendingSignableIds,
  polledSignables,
  pendingDecision,
  hookFunctionToPoll,
  isTransaction,
  onSuccess,
}: PendingSignaturesModalProps) {
  const { mockSign, isLoading: isMockSignLoading } = useMockSign();
  const [isProcessingMock, setIsProcessingMock] = useState(false);
  const [mockError, setMockError] = useState<string | null>(null);
  const [pendingCount, setPendingCount] = useState<number | null>(null);
  // Track the signatures we have seen for the user so we can handle cancellations
  const userSignatureIdsRef = useRef<Set<string>>(new Set());
  // Track signables where we have observed a completed signature so completion
  // can still resolve as "signed" if the signable disappears from the pending list.
  const signedSignableIdsRef = useRef<Set<string>>(new Set());
  const env = getFrontendEnvVariables();
  const { user } = useAuthClient();
  const currentUserId = user?.id ?? null;

  const isDevEnv = env.ENVIRONMENT === 'LOCAL';

  // Poll for updates with 1 second interval - but the data is being fetched in the parent component
  hookFunctionToPoll({
    refreshInterval: open ? 1000 : 0, // 1 second
  });

  /**
   * Clear the user signature ids when the modal is closed
   */
  useEffect(() => {
    if (!open) {
      setPendingCount(null);
      userSignatureIdsRef.current.clear();
      signedSignableIdsRef.current.clear();
      setIsProcessingMock(false);
      setMockError(null);
    }
  }, [open, pendingDecision, pendingSignableIds]);

  // Check if all pending signables have been signed
  useEffect(() => {
    if (!open || pendingSignableIds.length === 0 || !currentUserId) {
      return;
    }

    let hasAtLeastOneSignedSignature = false;
    // Check if all pending signables have the user's signature
    // Visit every child even while an earlier signature is pending so cancellation
    // can be recognized for any child on the next poll.
    const processed = pendingSignableIds.map((signableId) => {
      const signable = polledSignables.find(
        (p: PendingSignable) => p.id === signableId
      );
      // If the signable drops out of the pending list after we have seen it,
      // treat it as processed (cancelled or completed transition).
      if (!signable) {
        if (signedSignableIdsRef.current.has(signableId)) {
          hasAtLeastOneSignedSignature = true;
        }
        return userSignatureIdsRef.current.has(signableId);
      }

      if (pendingDecision === AccountTransactionDecision.INITIATE) {
        // For the initiator signature, check if it has been signed (has .signature) or canceled (not existing anymore)
        const sig: Signature | undefined = signable.initiatorSignature;
        if (sig) userSignatureIdsRef.current.add(signableId);
        const hasInitiatorSignature = sig?.signature != null;
        if (hasInitiatorSignature) {
          hasAtLeastOneSignedSignature = true;
          signedSignableIdsRef.current.add(signableId);
        }
        return (
          hasInitiatorSignature ||
          (!sig && userSignatureIdsRef.current.has(signableId))
        );
      } else if (pendingDecision === OrgOperationDecision.APPROVE) {
        // For every of the user's approvals for this signable, check if it has been signed (has .signature) or canceled (not existing anymore)
        // NOTE: The user may have multiple approvals if the proposal is across multiple networks
        const userApprovals = signable.approvals.filter(
          (sig: Signature) => sig.reviewingUserId === currentUserId
        );

        // Case: the user has approval  signatures, add to ref and return true if all signatures are present
        if (userApprovals.length > 0) {
          userSignatureIdsRef.current.add(signableId);
          const allSigned = userApprovals.every((sig: Signature) =>
            Boolean(sig.signature)
          );
          if (allSigned) {
            hasAtLeastOneSignedSignature = true;
            signedSignableIdsRef.current.add(signableId);
          }
          return allSigned;
        }

        // Case: no approvals, return true if we have previously seen the signable id (it was cancelled)
        return userSignatureIdsRef.current.has(signableId);
      } else if (pendingDecision === OrgOperationDecision.REJECT) {
        // For every of the user's rejections for this signable, check if it has been signed (has .signature) or canceled (not existing anymore)
        // NOTE: The user may have multiple rejections if the proposal is across multiple networks
        const userRejections = signable.rejections.filter(
          (sig: Signature) => sig.reviewingUserId === currentUserId
        );

        // Case: the user has rejection signatures, add to ref and return true if all signatures are present
        if (userRejections.length > 0) {
          userSignatureIdsRef.current.add(signableId);
          const allSigned = userRejections.every((sig: Signature) =>
            Boolean(sig.signature)
          );
          if (allSigned) {
            hasAtLeastOneSignedSignature = true;
            signedSignableIdsRef.current.add(signableId);
          }
          return allSigned;
        }

        // Case: no rejections, return true if we have previously seen the signable id (it was cancelled)
        return userSignatureIdsRef.current.has(signableId);
      }
      return false;
    });

    setPendingCount(processed.filter((done) => !done).length);

    // Case: All pending signables have been processed (signed or canceled)
    if (processed.every(Boolean)) {
      // Completion belongs to the whole requested action. A partial cancellation
      // must not announce a successfully queued batch, or hide an unsigned child.
      if (
        isTransaction &&
        pendingDecision === AccountTransactionDecision.INITIATE &&
        onSuccess
      ) {
        const signedChildren = pendingSignableIds.map((id) =>
          polledSignables.find((signable) => signable.id === id)
        );
        if (
          signedChildren.every((child) =>
            Boolean(child?.initiatorSignature?.signature)
          )
        ) {
          const approvalsNeeded = signedChildren.reduce(
            (sum, child) =>
              sum +
              (child
                ? Math.max(
                    child.requiredApprovals -
                      new Set(
                        child.approvals
                          .filter((signature) => Boolean(signature.signature))
                          .map((signature) => signature.reviewingUserId)
                      ).size,
                    0
                  )
                : 0),
            0
          );
          onSuccess(approvalsNeeded);
        }
      }

      setIsProcessingMock(false);
      refreshSignables();
      resetPendingState(hasAtLeastOneSignedSignature ? 'signed' : 'cancelled');
    }
  }, [
    open,
    polledSignables,
    pendingSignableIds,
    pendingDecision,
    currentUserId,
    resetPendingState,
    refreshSignables,
    setIsProcessingMock,
    onSuccess,
    isTransaction,
  ]);

  const handleMockDecision = async (action: 'CONFIRM' | 'CANCEL') => {
    if (!pendingDecision) return;

    setMockError(null);
    setIsProcessingMock(true);
    try {
      await Promise.all(
        pendingSignableIds.map((signableId) =>
          mockSign({ decision: pendingDecision, action, signableId })
        )
      );
      if (action === 'CANCEL') {
        // Unsigned transactions are hidden from the queue, so polling cannot
        // always observe their removal. The successful response confirms it.
        setIsProcessingMock(false);
        refreshSignables();
        resetPendingState('cancelled');
      }
      // Polling owns successful signing. A failed request must release the
      // buttons so it can be retried without recreating the transaction.
    } catch (error) {
      setMockError(
        `Local ${action === 'CONFIRM' ? 'signing' : 'cancellation'} failed. ${
          error instanceof Error ? error.message : 'Please try again.'
        }`
      );
      setIsProcessingMock(false);
    }
  };

  if (!pendingDecision) {
    return null;
  }

  const decisionTitleCase =
    pendingDecision === OrgOperationDecision.APPROVE
      ? 'Approve'
      : pendingDecision === OrgOperationDecision.REJECT
        ? 'Reject'
        : 'Initiate';
  const decisionNoun =
    pendingDecision === OrgOperationDecision.APPROVE
      ? 'approval'
      : pendingDecision === OrgOperationDecision.REJECT
        ? 'rejection'
        : 'transaction creation';

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) resetPendingState('cancelled');
      }}
    >
      <DialogContent
        className="sm:max-w-md z-[61]"
        dialogOverlayProps={{ className: 'z-[60]' }}
      >
        <DialogHeader className="text-center pt-4">
          <DialogTitle className="text-center mb-4">
            {decisionTitleCase}
            {isTransaction ? ' transaction' : ' operation'}
            {pendingSignableIds.length === 1 ? '' : 's'} in the mobile app
          </DialogTitle>
          <DialogDescription className="text-center">
            Please open the Den mobile app and sign
            {pendingSignableIds.length === 1 ? ' this ' : ' these '}
            {decisionNoun}
            {pendingSignableIds.length === 1 ? '' : 's'}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center justify-center py-6 gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <div className="text-sm text-muted-foreground text-center">
            {pendingCount ?? pendingSignableIds.length} signature
            {(pendingCount ?? pendingSignableIds.length) === 1 ? ' ' : 's '}
            pending
          </div>

          {/* DEV ONLY: This button should only be rendered in a dev environment */}
          {/* It puts in a fake signature, so we don't have to use the mobile app locally */}
          {/* In stage and prod, we will be checking for real signatures anyway though */}
          {isDevEnv && mockError && (
            <p role="alert" className="text-sm text-destructive text-center">
              {mockError}
            </p>
          )}
          {isDevEnv && (
            <div className="flex gap-2 mt-2">
              <Button
                onClick={() => void handleMockDecision('CONFIRM')}
                disabled={isMockSignLoading || isProcessingMock}
                variant="outline"
                size="sm"
              >
                {isProcessingMock ? 'Processing...' : 'DEV: Mock Sign'}
              </Button>
              <Button
                onClick={() => void handleMockDecision('CANCEL')}
                disabled={isMockSignLoading || isProcessingMock}
                variant="outline"
                size="sm"
              >
                {isProcessingMock ? 'Processing...' : 'DEV: Mock Cancel'}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
